import { ERROR_MESSSAGE } from "../../config";

/**
 * Object for validating and formatting before request is sent
 */
export const validateAndFormat = {
  /**
   * Validate and format firstName, lastName and timezone
   * @param {String} value
   */
  name(value, context = false, name = "name") {
    value = value === null ? "" : value;
    if (value.trim().length > 0) {
      return value.trim();
    }
    if (!ERROR_MESSSAGE[name]) {
      return "";
    }
    if (context) {
      context.dispatch("globalMessagePopup", ERROR_MESSSAGE[name], {
        root: true
      });
    }
    return false;
  },
  /**
   * Validate and format state code
   * @param {Number} value
   * @param {*} context
   */
  stateCode(value, context = false) {
    if (value <= -1 || value >= 52) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.stateCode, {
          root: true
        });
      }

      return false;
    }
    return value;
  },
  /**
   * Validate and format postal code
   * @param {String} value
   * @param {*} context
   */
  postalCode(value, context = false) {
    // Check if value is not entered
    if (value === "") {
      return "";
    }

    // If value is entered match it
    if (!value.trim().match(/^\d{5}(?:[-\s]\d{4})?$/)) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.postalCode, {
          root: true
        });
      }
      return false;
    }

    // Return the trimmed value
    return value.trim();
  },
  /**
   * Validate and format email
   * @param {String} value
   */
  email(value, context = false) {
    if (value === undefined || value === null) {
      return "";
    } else if (value.trim() === "") {
      return "";
    } else if (
      // eslint-disable-next-line
        value.trim().match(/^[\w\-\.\+]+\@[a-zA-Z0-9\.\-]+\.[a-zA-z0-9]{2,4}$/)
    ) {
      return value.trim();
    } else {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.email, {
          root: true
        });
      }
      return false;
    }
  },
  /**
   * Validate all emails
   * @param {Array} emails
   * @param {Object} context
   */
  allEmails(emails, context) {
    // Step 1: Default phone valid or not
    let emailValid = true;

    // Step 2: Map all the phones
    let email = emails.map(curEmail => {
      const curVal = this.email(curEmail, context);
      emailValid = emailValid & (curVal !== false);
      return curVal;
    });

    // Step 3: Check if all the phones are valid
    if (!emailValid) {
      email = false;
    } else {
      email = email.filter(curPhone => {
        return curPhone !== false && curPhone !== "";
      });
    }

    // Step 4: Return the phones
    return email;
  },
  /**
   * Validate and format phone number
   * @param {String} value
   */
  phone(value, context = false) {
    // Step 1: Set phone as false
    let phone = false;
    try {
      // Step 2: Split and join to get only the value
      phone = value.split("-").join("");

      // Step 3: Check if phone number value is interger
      if (value.trim() === "") {
        phone = "";
      } else if (isNaN(phone)) {
        phone = false; // Phone set to false if phone is not a number
      } else {
        phone = parseInt(phone); // Phone is a number

        // Step 4: Check if phone number is within the range
        if (phone < 1000000000 || phone > 9999999999) {
          phone = false; // Phone is set to false if not in range
        }
      }
    } catch (err) {
      phone = false;
    }

    // Step 5: Give error message
    if (context && phone === false) {
      context.dispatch("globalMessagePopup", ERROR_MESSSAGE.phone, {
        root: true
      });
    }

    // Step 6: Return phone
    return phone;
  },
  /**
   * Validate all the phones
   * @param {Array} phones
   * @param {Object} context
   */
  allPhones(phones, context) {
    // Step 1: Default phone valid or not
    let phoneValid = true;

    // Step 2: Map all the phones
    let phone = phones.map(curPhone => {
      const curVal = this.phone(curPhone, context);
      phoneValid = phoneValid & (curVal !== false);
      return curVal;
    });

    // Step 3: Check if all the phones are valid
    if (!phoneValid) {
      phone = false;
    } else {
      phone = phone.filter(curPhone => {
        return curPhone !== false && curPhone !== "";
      });
    }

    // Step 4: Return the phones
    return phone;
  },
  /**
   * Validate and format date
   * @param {Object} param0
   */
  date({ dayOfMonth, month, year }, context = false) {
    // Step 1: Check if date month and year are all greater than 0
    if (dayOfMonth === 0 && month === 0 && year === 0) {
      return {
        dayOfMonth: -1,
        month: -1,
        year: -1
      };
    }

    // Step 2: Check for valid day of birth
    if (dayOfMonth <= 0 || month < 0 || year < 0) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.dob, {
          root: true
        });
      }
      return false;
    }

    // Step 2: Future date of birth
    if (new Date(year, month, dayOfMonth).getTime() >= new Date().getTime()) {
      if (context) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.futureDob, {
          root: true
        });
      }
      return false;
    }

    return {
      dayOfMonth,
      month: month + 1,
      year
    };
  },
  /**
   * Validate and format group list
   * @param {Object} context
   */
  groupList(context) {
    let groupList = context.getters.getSelected.map(user => {
      return {
        id: user.id,
        type: user.type
      };
    });

    if (groupList.length <= 0) {
      groupList = false;
      context.dispatch("globalMessagePopup", ERROR_MESSSAGE.groupList, {
        root: true
      });
    }

    return [
      groupList,
      context.getters.getSelected.map(user => {
        return { ...user };
      })
    ];
  },
  /**
   * Validate and format the insurance company value
   * @param {Number} value
   * @param {Object} context
   */
  insuranceCompany(value, context) {
    if (!isNaN(value)) {
      return value;
    } else {
      context.dispatch("globalMessagePopup", ERROR_MESSSAGE.insuranceCompany, {
        root: true
      });
      return false;
    }
  },
  /**
   * Validate and format insurance id
   * @param {String} value
   * @param {Number} insuranceCompany
   * @param {Object} context
   */
  insuranceId(value, insuranceCompany, context) {
    if (insuranceCompany !== false && insuranceCompany !== 0) {
      if (value.trim().length <= 0) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.insuranceId, {
          root: true
        });
        return false;
      } else {
        return value.trim();
      }
    } else {
      return false;
    }
  },
  /**
   * Validate and format a simple int
   * @param {Number} value
   */
  simpleInt(value) {
    if (isNaN(value)) {
      return false;
    } else {
      return parseInt(value);
    }
  }
};
